/**
 * habit-derive.ts — Pure derive functions for Habit streak + completion metrics.
 *
 * Design decisions (D12):
 * - No stored fields: all stats computed from `completions: string[]` (YYYY-MM-DD).
 * - String comparison is safe for ISO dates (lexicographic = chronological).
 * - Future dates in completions are silently ignored (BR-HABIT-010).
 * - Duplicate dates in completions are deduplicated before all calculations.
 *
 * D14: habitsForGoal — filter habits by linkedGoalId for goal detail view.
 */

import type { Habit, LifeOsState } from "../types";
import { todayIso, addDaysIso } from "../lib/date";

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Deduplicate and filter out future dates, then sort ascending. */
function sanitizeCompletions(completions: string[], today: string): string[] {
  return [...new Set(completions)]
    .filter((d) => d <= today)
    .sort();
}

/** Subtract one calendar day from an ISO date string. */
function prevDay(isoDate: string): string {
  return addDaysIso(-1, new Date(`${isoDate}T00:00:00`));
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * habitStreakCurrent — consecutive days ending at today (or yesterday).
 *
 * Definition:
 *   Walk backwards from `today`:
 *   - If today is in completions → start counting from today.
 *   - If today is NOT in completions but yesterday is → start counting from yesterday
 *     (grace: hôm nay chưa tick vẫn giữ streak tới hôm qua).
 *   - Otherwise → streak = 0.
 *   Continue decrementing until a day is missing.
 */
export function habitStreakCurrent(completions: string[], today = todayIso()): number {
  const set = new Set(sanitizeCompletions(completions, today));

  // Determine starting point: today or yesterday (grace window)
  const yesterday = prevDay(today);
  let cursor = set.has(today) ? today : set.has(yesterday) ? yesterday : null;

  if (!cursor) return 0;

  let count = 0;
  while (set.has(cursor)) {
    count++;
    cursor = prevDay(cursor);
  }
  return count;
}

/**
 * habitStreakLongest — longest consecutive run in all completions history.
 *
 * Definition:
 *   Sort unique valid dates ascending; walk forward counting consecutive days
 *   (gap = exactly 1 day). Track the maximum run seen.
 */
export function habitStreakLongest(completions: string[], today = todayIso()): number {
  const sorted = sanitizeCompletions(completions, today);
  if (sorted.length === 0) return 0;

  let best = 1;
  let cur = 1;

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];
    // Check if consecutive: curr should equal prevDay advanced by 1
    const expectedNext = addDaysIso(1, new Date(`${prev}T00:00:00`));
    if (curr === expectedNext) {
      cur++;
      if (cur > best) best = cur;
    } else {
      cur = 1;
    }
  }
  return best;
}

/**
 * habitCompletionRate — completion % over a rolling N-week window (default 4 weeks).
 *
 * Formula:
 *   window = [today - (weeks*7 - 1), today] (inclusive, capped at today)
 *   rate = completionsInWindow / (targetPerWeek * weeks) * 100
 *   clamped to [0, 100]
 */
export function habitCompletionRate(
  habit: Pick<Habit, "completions" | "targetPerWeek">,
  weeks = 4,
  today = todayIso(),
): number {
  if (habit.targetPerWeek <= 0 || weeks <= 0) return 0;

  const windowStart = addDaysIso(-(weeks * 7 - 1), new Date(`${today}T00:00:00`));
  const set = new Set(sanitizeCompletions(habit.completions, today));

  let done = 0;
  // Iterate over each day in the window [windowStart, today]
  let cursor = windowStart;
  while (cursor <= today) {
    if (set.has(cursor)) done++;
    cursor = addDaysIso(1, new Date(`${cursor}T00:00:00`));
  }

  const target = habit.targetPerWeek * weeks;
  return Math.min(100, Math.round((done / target) * 100));
}

/**
 * habitWeekProgress — completions in the current ISO week vs targetPerWeek.
 *
 * Week = Monday..Sunday containing `today`.
 * Returns { done, target } where done ≤ target is NOT enforced (user may over-achieve).
 */
export function habitWeekProgress(
  habit: Pick<Habit, "completions" | "targetPerWeek">,
  today = todayIso(),
): { done: number; target: number } {
  const todayDate = new Date(`${today}T00:00:00`);
  const dow = (todayDate.getDay() + 6) % 7; // 0 = Monday
  const monday = addDaysIso(-dow, todayDate);
  const sunday = addDaysIso(6 - dow, todayDate);

  const set = new Set(sanitizeCompletions(habit.completions, today));
  let done = 0;
  let cursor = monday;
  while (cursor <= sunday) {
    if (set.has(cursor)) done++;
    cursor = addDaysIso(1, new Date(`${cursor}T00:00:00`));
  }

  return { done, target: habit.targetPerWeek };
}

/**
 * habitsForGoal — D14: return habits linked to a specific goal.
 *
 * Pure function — safe when goal has been deleted (returns []).
 * goalId must be non-empty for a match; habits without linkedGoalId are skipped.
 */
export function habitsForGoal(state: Pick<LifeOsState, "habits">, goalId: string): Habit[] {
  if (!goalId) return [];
  return state.habits.filter((h) => h.linkedGoalId === goalId);
}

/**
 * habitStats — convenience bundle: current streak, longest streak, completion rate.
 */
export function habitStats(
  habit: Pick<Habit, "completions" | "targetPerWeek">,
  today = todayIso(),
): { streakCurrent: number; streakLongest: number; completionRate: number } {
  return {
    streakCurrent: habitStreakCurrent(habit.completions, today),
    streakLongest: habitStreakLongest(habit.completions, today),
    completionRate: habitCompletionRate(habit, 4, today),
  };
}
